import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Upload, X } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Btn, Card, Field, Input, Select } from "../components/common";

const KEY = "fmis_reg";
const BLANK = { first_name: "", middle_name: "", last_name: "", suffix: "", email: "", contact: "", barangay: "Mabuhay", address: "", gkk_id: "", unit_id: "", position: "",
  election_date: "", term_start: "", term_end: "", confirmed_by: "", appointment_status: "", auth_method: "password", certify: false, privacy: false, website: "" };
const STEPS = ["Personal information", "Official designation", "Proof of authority", "Declaration"];
const DECL = "I certify that the information I have provided is true, correct, and complete. I further declare that I am currently serving in the GKK or Unit and position indicated in this application. I understand that providing false information or attempting to gain unauthorized access to financial records may result in suspension or denial of access.";

function Err({ msg }) { return msg ? <p className="text-sm text-[#B91C1C] mt-1">{msg}</p> : null; }

function GkkPicker({ dir, f, set }) {
  const [q, setQ] = useState("");
  const list = dir.gkks.filter((g) => !q || `${g.name} ${(g.address || "").replace(/,?\s*(Barangay Mabuhay|General Santos City)/gi, "")}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <Field label="Name of GKK (official list)">
      <Input placeholder="Type to search, e.g. San Isidro" value={q} onChange={(e) => setQ(e.target.value)} data-testid="reg-gkk-search" className="mb-2" />
      <Select value={f.gkk_id} onChange={(e) => set({ gkk_id: e.target.value, unit_id: "" })} data-testid="reg-gkk-select">
        <option value="">— Choose your GKK ({list.length}) —</option>
        {list.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
      </Select>
    </Field>
  );
}

function Personal({ f, set, err, dir }) {
  const inp = (k, label, props = {}) => <Field label={label}><Input data-testid={`reg-${k}`} value={f[k]} onChange={(e) => set({ [k]: e.target.value })} {...props} /><Err msg={err[k]} /></Field>;
  const units = dir.units.filter((u) => u.gkk_id === f.gkk_id);
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {inp("first_name", "First name")}{inp("middle_name", "Middle name (optional)")}{inp("last_name", "Last name")}{inp("suffix", "Suffix (optional, e.g. Jr.)")}
      {inp("email", "Active Google / email address", { type: "email", autoCapitalize: "none", inputMode: "email" })}{inp("contact", "Contact number", { inputMode: "tel" })}
      {inp("barangay", "Residential barangay")}{inp("address", "Complete address or Purok/Sitio")}
      <div className="sm:col-span-2"><GkkPicker dir={dir} f={f} set={set} /><Err msg={err.gkk_id} /></div>
      <div className="sm:col-span-2"><Field label="Name of Unit (if applicable)"><Select value={f.unit_id} onChange={(e) => set({ unit_id: e.target.value })} disabled={!f.gkk_id} data-testid="reg-unit-select">
        <option value="">— Not applicable / choose Unit —</option>{units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</Select></Field><Err msg={err.unit_id} /></div>
    </div>
  );
}

function Designation({ f, set, err, dir }) {
  const d = (k, label) => <Field label={label}><Input type="date" data-testid={`reg-${k}`} value={f[k]} onChange={(e) => set({ [k]: e.target.value })} /><Err msg={err[k]} /></Field>;
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="sm:col-span-2"><Field label="Official position"><Select value={f.position} onChange={(e) => set({ position: e.target.value })} data-testid="reg-position-select">
        <option value="">— Choose your position —</option>{dir.positions.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}</Select></Field><Err msg={err.position} /></div>
      {d("election_date", "Date of election or appointment")}{d("term_start", "Beginning of term of office")}{d("term_end", "Expected end of term (if known)")}
      <Field label="Appointed / confirmed by (if applicable)"><Input data-testid="reg-confirmed_by" value={f.confirmed_by} onChange={(e) => set({ confirmed_by: e.target.value })} /></Field>
      <div className="sm:col-span-2"><Field label="Current status"><Select value={f.appointment_status} onChange={(e) => set({ appointment_status: e.target.value })} data-testid="reg-status-select">
        <option value="">— Choose —</option>{["Newly Elected", "Appointed", "Continuing", "Acting/Interim"].map((s) => <option key={s}>{s}</option>)}</Select></Field><Err msg={err.appointment_status} /></div>
      <div className="sm:col-span-2"><p className="font-semibold mb-2">How will you sign in?</p>
        {[["password", "Email and a separate GKK-FMIS password", "You will create the password on this website after verifying your email."], ["google", "Sign in with Google", "Use your Google account; GKK-FMIS never sees your Google password."]].map(([v, a, b]) => (
          <label key={v} className={`flex gap-3 rounded-xl border-2 p-3 mb-2 cursor-pointer ${f.auth_method === v ? "border-[#5C4033] bg-[#F3EDE2]" : "border-[#E4DACD]"}`}>
            <input type="radio" name="auth" checked={f.auth_method === v} onChange={() => set({ auth_method: v })} className="mt-1 h-5 w-5" data-testid={`reg-auth-${v}`} /><span><b>{a}</b><br /><span className="text-sm text-[#6E6259]">{b}</span></span></label>))}
      </div>
    </div>
  );
}

function Proof({ files, setFiles }) {
  const add = (list) => {
    const ok = Array.from(list || []).filter((x) => /^(image\/(jpeg|png)|application\/pdf)$/.test(x.type) && x.size <= 5 * 1024 * 1024);
    setFiles([...files, ...ok].slice(0, 5));
  };
  return (
    <div className="space-y-3">
      <p className="text-sm">Optional but helpful: certification of election or appointment, official list of officers, certification from the GKK President, or authorization from the Parish Office. PDF, JPG or PNG, up to 5 MB each. Please do not upload government IDs.</p>
      <label className="inline-flex"><input type="file" multiple accept="image/jpeg,image/png,application/pdf" className="hidden" onChange={(e) => add(e.target.files)} data-testid="reg-proof-input" />
        <span className="h-12 px-5 rounded-full border-2 border-[#5C4033] font-semibold inline-flex items-center gap-2 cursor-pointer"><Upload className="h-5 w-5" /> Choose files</span></label>
      {files.map((x, i) => <div key={i} className="flex items-center justify-between rounded-xl bg-white border border-[#E4DACD] px-3 py-2 text-sm" data-testid={`reg-proof-${i}`}>{x.name} · {Math.ceil(x.size / 1024)} KB
        <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label="Remove file"><X className="h-4 w-4" /></button></div>)}
    </div>
  );
}

function Declaration({ f, set }) {
  return (
    <div className="space-y-3">
      <label className="flex gap-3 rounded-xl border border-[#E4DACD] bg-white p-4"><input type="checkbox" className="mt-1 h-6 w-6 shrink-0" checked={f.certify} onChange={(e) => set({ certify: e.target.checked })} data-testid="reg-certify" /><span>{DECL}</span></label>
      <label className="flex gap-3 rounded-xl border border-[#E4DACD] bg-white p-4"><input type="checkbox" className="mt-1 h-6 w-6 shrink-0" checked={f.privacy} onChange={(e) => set({ privacy: e.target.checked })} data-testid="reg-privacy" />
        <span>I have read the Parish Data Privacy Notice: my personal information is collected only to verify my office and manage my GKK-FMIS access, is kept confidential under Republic Act No. 10173 (Data Privacy Act of 2012), and I accept my responsibility to protect the confidential financial information I will handle.</span></label>
    </div>
  );
}

function validate(step, f, dir) {
  const e = {};
  const need = (k, m = "Required") => { if (!String(f[k] || "").trim()) e[k] = m; };
  if (step === 0) {
    ["first_name", "last_name", "contact", "barangay", "address"].forEach((k) => need(k));
    if (!/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(f.email.trim())) e.email = "Enter a valid email address";
    need("gkk_id", "Choose your GKK");
  }
  if (step === 1) {
    need("position", "Choose your position"); need("election_date"); need("term_start"); need("appointment_status", "Choose your status");
    if (dir.positions.find((p) => p.key === f.position)?.unit && !f.unit_id) e.position = "This position needs a Unit — go back and choose your Unit";
    if (f.term_end && f.term_end < f.term_start) e.term_end = "End of term cannot be before the start";
  }
  return e;
}

export default function Register() {
  const [dir, setDir] = useState(null);
  const [f, setF] = useState(() => ({ ...BLANK, ...JSON.parse(sessionStorage.getItem(KEY) || "{}") }));
  const [step, setStep] = useState(0);
  const [err, setErr] = useState({});
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [fail, setFail] = useState("");
  useEffect(() => { api.get("/public/directory").then((r) => setDir(r.data)).catch((e) => setFail(errMsg(e))); }, []);
  useEffect(() => { sessionStorage.setItem(KEY, JSON.stringify(f)); }, [f]);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const ready = useMemo(() => f.certify && f.privacy, [f]);
  if (!dir) return <div className="min-h-screen flex items-center justify-center">{fail || <Loader2 className="h-8 w-8 animate-spin" />}</div>;
  const next = () => { const e = validate(step, f, dir); setErr(e); if (!Object.keys(e).length) setStep(step + 1); };
  const submit = async () => {
    if (busy) return;
    setBusy(true); setFail("");
    const fd = new FormData();
    fd.append("payload", JSON.stringify({ ...f, email: f.email.trim(), origin: window.location.origin }));
    files.forEach((x) => fd.append("files", x));
    try { const { data } = await api.post("/registrations", fd); sessionStorage.removeItem(KEY); setDone(data); } catch (e) { setFail(errMsg(e)); } finally { setBusy(false); }
  };
  if (done) return (
    <div className="min-h-screen bg-[#FAF7F2] px-5 py-10"><Card className="max-w-lg mx-auto text-center space-y-3" data-testid="reg-done">
      <CheckCircle2 className="h-12 w-12 mx-auto text-[#2D6A4F]" /><h1 className="font-serif text-2xl font-bold">Registration received</h1>
      <p>Your reference number is <b data-testid="reg-ref-no">{done.ref_no}</b>. Please write it down.</p>
      <p>We sent a verification link to <b>{done.email}</b>. Open it within 60 minutes to verify your email{f.auth_method === "password" ? " and create your GKK-FMIS password" : ""}.</p>
      <p className="text-sm text-[#6E6259]">After that, the Parish Administrator will review your designation before you can use the system.</p>
      <Link to="/login" className="font-semibold text-[#C85A32]">← Back to the front page</Link></Card></div>
  );
  const Body = [Personal, Designation, null, null][step];
  return (
    <div className="min-h-screen bg-[#FAF7F2] px-4 py-8">
      <div className="max-w-2xl mx-auto space-y-5">
        <Link to="/login" className="font-semibold text-[#C85A32]" data-testid="reg-back-home">← Front page</Link>
        <h1 className="font-serif text-3xl font-bold">Register as GKK/Unit Officer</h1>
        <p className="text-sm text-[#6E6259]">Step {step + 1} of 4 · {STEPS[step]}</p>
        <div className="flex gap-1">{STEPS.map((s, i) => <div key={s} className={`h-2 flex-1 rounded ${i <= step ? "bg-[#5C4033]" : "bg-[#E4DACD]"}`} />)}</div>
        <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" value={f.website} onChange={(e) => set({ website: e.target.value })} />
        <Card data-testid={`reg-step-${step}`}>
          {Body && <Body f={f} set={set} err={err} dir={dir} />}
          {step === 2 && <Proof files={files} setFiles={setFiles} />}
          {step === 3 && <Declaration f={f} set={set} />}
        </Card>
        {fail && <p className="rounded-xl bg-[#FEF2F2] text-[#B91C1C] p-3 font-semibold" data-testid="reg-error">{fail}</p>}
        <div className="flex gap-3">
          {step > 0 && <Btn v="ghost" onClick={() => setStep(step - 1)} data-testid="reg-prev">Back</Btn>}
          {step < 3 ? <Btn className="flex-1" onClick={next} data-testid="reg-next">Next</Btn>
            : <Btn v="green" className="flex-1" disabled={!ready || busy} loading={busy} onClick={submit} data-testid="reg-submit">Submit registration</Btn>}
        </div>
      </div>
    </div>
  );
}
